import { createFileRoute, redirect } from '@tanstack/react-router'
import { databases } from '@/lib/appwrite'
import { Query } from 'appwrite'
import type { Instrument } from '@/Wist-blockchain/collections/instruments/types'
import type { Company } from '@/lib/collections/organizations/getOrganizationsFetch'
import type { ServiceReport } from '@/Wist-blockchain/collections/serviceReport/types'
import AddServiceReport from '@/Wist-blockchain/serviceReport/view/user/addServiceReport'

const DB = import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID
const COL_REPORT = import.meta.env.VITE_BLOCKCHAIN_SERVICE_REPORT_COLLECTION_ID
const COL_ACTIVITIES = import.meta.env.VITE_BLOCKCHAIN_REPORT_ACTIVITIES_COLLECTION_ID
const COL_MATERIALS = import.meta.env.VITE_BLOCKCHAIN_REPORT_MATERIALS_COLLECTION_ID

export const Route = createFileRoute('/_authenticated/instruments/serviceReport/$id/revision/$reportDocId/')({
    beforeLoad: ({ context }) => {
        if (!context.user) throw redirect({ to: '/login' })
        const { isServicioTecnico, isAdmin } = context.userRoles
        if (!isServicioTecnico && !isAdmin) throw redirect({ to: '/unauthorized' })
    },
    loader: async ({ params }) => {
        const { id, reportDocId } = params

        const instrument = await databases.getDocument(
            DB,
            import.meta.env.VITE_BLOCKCHAIN_INSTRUMENTS_COLLECTION_ID,
            id
        ) as unknown as Instrument

        const company = instrument.idOrganization
            ? await databases.getDocument(
                import.meta.env.VITE_APPWRITE_DATABASE_ID,
                import.meta.env.VITE_ORGANIZATIONS_COLLECTION_ID,
                instrument.idOrganization
            ) as unknown as Company
            : undefined

        // El informe puede ser el original o una revisión previa
        const reportToBase = await databases.getDocument(DB, COL_REPORT, reportDocId) as unknown as ServiceReport

        // El idReport original siempre es el parentReportId (si es revisión) o el propio idReport
        const originalIdReport = reportToBase.parentReportId ?? reportToBase.idReport

        // Contar revisiones existentes del original para determinar el siguiente número
        const existingRevisions = await databases.listDocuments(DB, COL_REPORT, [
            Query.equal("parentReportId", originalIdReport),
            Query.limit(1),
        ])
        const nextRevisionNumber = existingRevisions.total + 1

        // Cargar actividades y materiales del informe base
        const [acts, mats] = await Promise.all([
            databases.listDocuments(DB, COL_ACTIVITIES, [
                Query.equal("idReport", reportToBase.idReport),
                Query.orderAsc("order"),
            ]),
            databases.listDocuments(DB, COL_MATERIALS, [
                Query.equal("idReport", reportToBase.idReport),
                Query.orderAsc("order"),
            ]),
        ])

        return {
            instrument,
            company,
            reportToBase,
            originalIdReport,
            nextRevisionNumber,
            initialActivities: acts.documents,
            initialMaterials: mats.documents,
        }
    },
    errorComponent: () => <div>Informe no encontrado.</div>,
    component: RouteComponent,
})

function RouteComponent() {
    const {
        instrument,
        company,
        reportToBase,
        originalIdReport,
        nextRevisionNumber,
        initialActivities,
        initialMaterials,
    } = Route.useLoaderData()

    return (
        <AddServiceReport
            instrument={instrument}
            company={company}
            initialDraft={reportToBase}
            initialActivities={initialActivities}
            initialMaterials={initialMaterials}
            revisionMode={{ originalIdReport, nextRevisionNumber }}
        />
    )
}
